/**
 * CutsceneTriggerWatcher.tsx — WEB.4.CS
 *
 * Headless side-effect component (returns null) that watches store
 * transitions and fires the matching cutscene. Mounted at App root so it
 * survives gameplay-shell conditional rendering (it doesn't depend on
 * gamePhase=playing for game-over endings).
 *
 * Trigger table mirrors GDG/Cutscene-Script-And-Prompts.md §1:
 *
 *   - era 1→2 / 2→3 / 3→4 in endTurn → transition-N cutscene
 *   - gameStatus playing→ended w/ endReason X → ending-Y cutscene
 *
 * Intro is NOT fired here — it's fired directly by `startGame()` because
 * timing relative to gamePhase=menu→playing matters.
 */

import { useEffect, useRef } from "react";
import { useGameStore } from "../../store/game-store.js";

/** Map gameplay endReason → cutscene group id. Returns null for unhandled.
 *
 * R9 mapping:
 *   renewable-pathway    → ending-A (cooperative win)
 *   green-future         → ending-A (legacy alias, kept for save compat)
 *   bankruptcy-solo-survivor / economic-hegemony → ending-B (solo economic win)
 *   round-limit          → ending-B (top-score economic dominance)
 *   tipping-point        → ending-C (all lose, tipping point)
 *   global-warming       → ending-C (legacy alias)
 *   mutual-destruction   → ending-D (all bankrupt)
 */
export function _endReasonToCutscene(
  reason: string | null,
): "ending-A" | "ending-B" | "ending-C" | "ending-D" | null {
  switch (reason) {
    case "renewable-pathway":
    case "green-future": // legacy alias
      return "ending-A";
    case "bankruptcy-solo-survivor":
    case "economic-hegemony":
    case "round-limit":
      return "ending-B";
    case "tipping-point":
    case "global-warming": // legacy alias
      return "ending-C";
    case "mutual-destruction":
      return "ending-D";
    default:
      return null;
  }
}

/** True when a cutscene group represents an era transition (not an ending or intro). */
function _isTransitionGroup(
  groupId:
    | "intro"
    | "transition-1"
    | "transition-2"
    | "transition-3"
    | "ending-A"
    | "ending-B"
    | "ending-C"
    | "ending-D"
    | null,
): boolean {
  return groupId === "transition-1" || groupId === "transition-2" || groupId === "transition-3";
}

export function CutsceneTriggerWatcher(): null {
  const era = useGameStore((s) => s.era);
  const gameStatus = useGameStore((s) => s.gameStatus);
  const endReason = useGameStore((s) => s.endReason);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const playCutscene = useGameStore((s) => s.playCutscene);
  const gamePhase = useGameStore((s) => s.gamePhase);
  const cutsceneGroupId = useGameStore((s) => s.cutsceneGroupId);
  const startPostCutsceneDialogue = useGameStore((s) => s.startPostCutsceneDialogue);

  // Track the previous values so transitions fire exactly once.
  const prevEraRef = useRef(era);
  const prevStatusRef = useRef(gameStatus);
  // Track the last active cutscene group so we can detect transition-N → null.
  const prevCutsceneGroupRef = useRef(cutsceneGroupId);

  useEffect(() => {
    if (gamePhase !== "playing") {
      // Reset era tracker when game returns to menu so the next session can
      // re-fire era transitions cleanly. gameStatus tracker resets in the
      // other effect — separation keeps each effect's deps minimal.
      prevEraRef.current = era;
      return;
    }
    const prevEra = prevEraRef.current;
    if (era !== prevEra) {
      if (prevEra === 1 && era === 2) playCutscene("transition-1");
      else if (prevEra === 2 && era === 3) playCutscene("transition-2");
      else if (prevEra === 3 && era === 4) playCutscene("transition-3");
    }
    prevEraRef.current = era;
  }, [era, gamePhase, playCutscene]);

  useEffect(() => {
    if (gamePhase !== "playing") {
      prevStatusRef.current = gameStatus;
      return;
    }
    if (prevStatusRef.current === "playing" && gameStatus === "ended") {
      // Mutual destruction = ALL seats bankrupt. Wins over the endReason map
      // because the store may have flagged "bankruptcy-solo-survivor" while
      // every seat is in lostSeats (defensive check).
      const cutscene = lostSeats.size >= 4 ? "ending-D" : _endReasonToCutscene(endReason);
      if (cutscene !== null) playCutscene(cutscene);
    }
    prevStatusRef.current = gameStatus;
  }, [gamePhase, gameStatus, endReason, lostSeats, playCutscene]);

  // R8 #6 — Detect era-transition cutscene completion (non-null → null) and
  // fire post-cutscene dialogue chain. Only fires for transition-N groups.
  useEffect(() => {
    const prev = prevCutsceneGroupRef.current;
    prevCutsceneGroupRef.current = cutsceneGroupId;

    if (cutsceneGroupId === null && _isTransitionGroup(prev) && gamePhase === "playing") {
      // The current `era` value already reflects the new era (cutscene fired
      // AFTER the era incremented in endTurn). Use it directly.
      startPostCutsceneDialogue(prev as "transition-1" | "transition-2" | "transition-3", era);
    }
  }, [cutsceneGroupId, era, gamePhase, startPostCutsceneDialogue]);

  return null;
}
